import React, { useEffect, useRef, useCallback, useState } from 'react';

/**
 * Cloudflare Turnstile Widget Component
 * 
 * This renders an invisible CAPTCHA alternative that protects against bots
 * without user friction. It calls onVerify with the token when verification succeeds.
 * 
 * @see https://developers.cloudflare.com/turnstile/
 */
const Turnstile = ({ onVerify, onError, onExpire }) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
  const [errorCount, setErrorCount] = useState(0);
  const [_isRetrying, setIsRetrying] = useState(false);
  const lastErrorTimeRef = useRef(0);
  const MAX_RETRIES = 3;
  const ERROR_COOLDOWN_MS = 5000; // 5 second cooldown between error toasts
  
  // Test keys only work with 'normal' or 'compact' size
  const isTestKey = siteKey?.startsWith('1x') || siteKey?.startsWith('2x') || siteKey?.startsWith('3x');

  const handleCallback = useCallback((token) => {
    if (onVerify) {
      onVerify(token);
    }
  }, [onVerify]);

  const handleError = useCallback(() => {
    const now = Date.now();
    // Debounce error handling to prevent spam
    if (now - lastErrorTimeRef.current < ERROR_COOLDOWN_MS) {
      return;
    }
    lastErrorTimeRef.current = now;
    
    setErrorCount(prev => {
      const newCount = prev + 1;
      if (newCount >= MAX_RETRIES) {
        // After max retries, allow form submission without turnstile in dev
        if (process.env.NODE_ENV === 'development') {
          console.warn('Turnstile: Max retries reached, auto-verifying for development');
          if (onVerify) onVerify('dev-fallback-after-errors');
          return 0;
        }
      }
      return newCount;
    });
    
    if (onError) {
      onError();
    }
    
    // Auto-retry after cooldown
    if (errorCount < MAX_RETRIES - 1) {
      setIsRetrying(true);
      setTimeout(() => {
        if (widgetIdRef.current && window.turnstile) {
          try {
            window.turnstile.reset(widgetIdRef.current);
          } catch (e) {
            console.warn('Turnstile reset failed:', e);
          }
        }
        setIsRetrying(false);
      }, 2000);
    }
  }, [onError, onVerify, errorCount]);

  const handleExpire = useCallback(() => {
    if (onExpire) {
      onExpire();
    }
  }, [onExpire]);

  useEffect(() => {
    // Don't render if no site key is configured
    if (!siteKey) {
      console.warn('Turnstile: REACT_APP_TURNSTILE_SITE_KEY not configured');
      // In development without key, auto-verify
      if (onVerify) {
        onVerify('dev-mode-no-turnstile');
      }
      return;
    }

    // Load Turnstile script if not already loaded
    const loadScript = () => {
      return new Promise((resolve, reject) => {
        if (window.turnstile) {
          resolve();
          return;
        }

        const existingScript = document.querySelector('script[src*="turnstile"]');
        if (existingScript) {
          existingScript.addEventListener('load', resolve);
          existingScript.addEventListener('error', reject);
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.defer = true;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    };

    const renderWidget = async () => {
      try {
        await loadScript();
        
        // Wait a bit for turnstile to be ready
        await new Promise(resolve => setTimeout(resolve, 100));

        if (containerRef.current && window.turnstile && !widgetIdRef.current) {
          widgetIdRef.current = window.turnstile.render(containerRef.current, {
            sitekey: siteKey,
            callback: handleCallback,
            'error-callback': handleError,
            'expired-callback': handleExpire,
            theme: 'light',
            // Test keys only support 'normal' or 'compact', production uses 'invisible'
            size: isTestKey ? 'normal' : 'invisible',
          });
        }
      } catch (error) {
        console.error('Failed to load Turnstile:', error);
        handleError();
      }
    };

    renderWidget();

    // Cleanup on unmount
    return () => {
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // Ignore cleanup errors
        }
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, isTestKey, handleCallback, handleError, handleExpire, onVerify]);

  // Reset the widget (call this if you need to re-verify)
  const _reset = useCallback(() => {
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  }, []);

  // Don't render anything visible if no site key
  if (!siteKey) {
    return null;
  }

  return (
    <div 
      ref={containerRef} 
      className="cf-turnstile"
      data-testid="turnstile-widget"
    />
  );
};

export default Turnstile;
